import { useCallback } from 'react';
import { isEqual } from 'lodash';
import { Field, FieldRenderProps, RenderableProps } from 'react-final-form';

import {
  RetailerNetworkLocationGroupFilter,
  RetailerNetworkLocationGroupFragment,
} from '@occc/graphql';
import {
  AutocompleteProps,
  FormInputProps,
  LazyAutocomplete,
  showErrorOnBlurOrDirty,
} from '@occc-app/design-system';

import { useGetRetailerNetworkLocationGroupsLazy } from '../../data-access/queries';

type RetailerNetworkLocationGroupSelectContainerProps = Pick<
  AutocompleteProps,
  'onFocus' | 'onBlur'
> & {
  value: RetailerNetworkLocationGroupFragment[];
  onChange: (values: RetailerNetworkLocationGroupFragment[]) => void;
  disabled?: boolean;
  loading?: boolean;
  label?: string;
  errorText?: string;
  inputProps?: FormInputProps;
  retailerNetworkUuidEquals: string;
  openCommerceUserUuidEquals?: string;
};

type RetailerNetworkLocationGroupMultipleSelectFieldProps = {
  children?: React.ReactNode;
  name: string;
  label: string;
} & Pick<
  RetailerNetworkLocationGroupSelectContainerProps,
  'inputProps' | 'retailerNetworkUuidEquals' | 'openCommerceUserUuidEquals'
>;

const makeFilter = ({
  retailerNetworkUuidEquals,
  openCommerceUserUuidEquals,
  search,
}: RetailerNetworkLocationGroupFilter): RetailerNetworkLocationGroupFilter => ({
  retailerNetworkUuidEquals,
  openCommerceUserUuidEquals,
  search: search || null,
});

const mapQueryResponseToRawArray = (
  response: ReturnType<typeof useGetRetailerNetworkLocationGroupsLazy>['response'],
) => response.data || [];

const mapRawResponseItemToAutocompleteOption = (
  locationGroup: RetailerNetworkLocationGroupFragment,
) => ({
  value: locationGroup.uuid,
  label: locationGroup.name,
  _raw: locationGroup,
});

const LocationGroupSelect: React.FC<
  React.PropsWithChildren<RetailerNetworkLocationGroupSelectContainerProps>
> = ({ retailerNetworkUuidEquals, openCommerceUserUuidEquals, ...restProps }) => (
  <LazyAutocomplete<
    RetailerNetworkLocationGroupFragment,
    ReturnType<typeof useGetRetailerNetworkLocationGroupsLazy>['response']
  >
    label='LocationGroups'
    {...restProps}
    useLazyQuery={useGetRetailerNetworkLocationGroupsLazy}
    makeQueryFilter={(search: string) =>
      makeFilter({ retailerNetworkUuidEquals, search, openCommerceUserUuidEquals })
    }
    mapQueryResponseToRawArray={mapQueryResponseToRawArray}
    mapRawResponseItemToAutocompleteOption={mapRawResponseItemToAutocompleteOption}
  />
);

export const RetailerNetworkLocationGroupMultipleSelectField: React.FC<
  RetailerNetworkLocationGroupMultipleSelectFieldProps
> = (props) => {
  const renderInput = useCallback<
    NonNullable<RenderableProps<FieldRenderProps<RetailerNetworkLocationGroupFragment[]>>['render']>
  >(
    ({ input, meta }) => (
      <LocationGroupSelect
        {...props}
        value={input.value || []}
        onFocus={input.onFocus}
        onBlur={input.onBlur}
        onChange={input.onChange}
        errorText={showErrorOnBlurOrDirty({ meta }) ? meta.error : undefined}
      />
    ),
    [props],
  );

  return <Field name={props.name} isEqual={isEqual} render={renderInput} />;
};
